import type { AxiosResponse } from 'axios';
import type { StreamClient } from './client';

export type RateLimit = {
  rate_limit?: number;
  rate_limit_remaining?: number;
  rate_limit_reset?: Date;
};

export type RequestMetadata = {
  response_headers: Record<string, string>;
  rate_limit: RateLimit;
  response_code: number;
  client_request_id: string;
};

export type StreamResponse<T> = T & {
  metadata: RequestMetadata;
};

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export class ApiClient {
  private readonly streamClient: StreamClient;

  constructor(streamClient: StreamClient) {
    this.streamClient = streamClient;
  }

  sendRequest = async <T>(
    method: HttpMethod,
    pathTemplate: string,
    pathParams?: Record<string, string>,
    queryParams?: Record<string, unknown>,
    body?: unknown,
    contentType: string = 'application/json',
  ): Promise<StreamResponse<T>> => {
    const response = await this.streamClient.doAxiosRequest<T>(
      method.toLowerCase(),
      buildRequestUrl(this.streamClient.baseURL, pathTemplate, pathParams),
      body,
      {
        params: queryParams,
        headers: { 'Content-Type': contentType },
      },
    );

    return { ...response.data, metadata: buildRequestMetadata(response) };
  };
}

const buildRequestUrl = (
  baseURL: string,
  pathTemplate: string,
  pathParams: Record<string, string> = {},
): string => {
  const path = Object.entries(pathParams).reduce(
    (acc, [name, value]) =>
      acc.replaceAll(`{${name}}`, encodeURIComponent(value)),
    pathTemplate,
  );
  return baseURL + path;
};

const buildRequestMetadata = (response: AxiosResponse): RequestMetadata => {
  const responseHeaders = toStringRecord(response.headers);
  const requestHeaders = toStringRecord(response.config?.headers);

  const rateLimit: RateLimit = {};
  const limit = parseInteger(responseHeaders['x-ratelimit-limit']);
  if (limit !== undefined) rateLimit.rate_limit = limit;
  const remaining = parseInteger(responseHeaders['x-ratelimit-remaining']);
  if (remaining !== undefined) rateLimit.rate_limit_remaining = remaining;
  const reset = parseInteger(responseHeaders['x-ratelimit-reset']);
  if (reset !== undefined) rateLimit.rate_limit_reset = new Date(reset * 1000);

  return {
    response_headers: responseHeaders,
    rate_limit: rateLimit,
    response_code: response.status,
    client_request_id: requestHeaders['x-client-request-id'] ?? '',
  };
};

const toStringRecord = (headers: unknown): Record<string, string> => {
  if (!headers || typeof headers !== 'object') return {};
  return Object.fromEntries(
    Object.entries(headers)
      .filter(([, value]) => value != null)
      .map(([key, value]) => [key, String(value)]),
  );
};

const parseInteger = (value: string | undefined): number | undefined => {
  if (value === undefined) return undefined;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};
